import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError, type CompetitionRole, type ScopedCompetitionMember } from '../lib/api';
import { Card } from './Card';
import { PageSpinner } from './Spinner';
import { DiscordMemberPicker } from './DiscordMemberPicker';
import { cn } from '../lib/cn';
import { MORPH } from '../lib/motion';
import { Plus, RefreshCw, Trash2, ShieldCheck } from 'lucide-react';

// Moved here from Organizations.tsx (comp-scoped tab in the Setup cockpit —
// see TournamentSetup.tsx 'members' tool). Behaviour is unchanged from the
// /orgs page version; only the location + the fixed compId/orgId context
// changed (no in-panel competition selector any more — the outer cockpit's
// competition selector drives `compId`).

const COMP_ROLE_OPTIONS: CompetitionRole[] = ['COMP_OWNER', 'COMP_ADMIN', 'COMP_MEMBER'];

// Never show a raw discordId — same shortId idiom as OrgMembersPanel/
// OrgProposalsPanel/OrgClansPanel/SeedingTablePanel/PublicMatchCard/UserLookup.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

function CompetitionMemberRow({
  compId,
  member,
  onDone,
}: {
  compId: string;
  member: ScopedCompetitionMember;
  onDone: () => void;
}) {
  const [role, setRole] = useState<CompetitionRole>(member.role);
  const [error, setError] = useState('');
  const updateMut = useMutation({
    mutationFn: () => api.updateCompetitionMemberRole(compId, member.discordId, role),
    onSuccess: () => {
      setError('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });
  const removeMut = useMutation({
    mutationFn: () => api.removeCompetitionMember(compId, member.discordId),
    onSuccess: () => {
      setError('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className="rounded-lg bg-white/[0.04] p-4">
      <div className="grid gap-3 md:grid-cols-[1fr_12rem_auto_auto] md:items-center">
        <div>
          <p className="text-sm font-semibold text-ink-primary">{member.displayName ?? shortId(member.discordId)}</p>
          <p className="text-xs text-ink-meta">{member.assignedBy ? `Assigned by ${member.assignedByName ?? shortId(member.assignedBy)}` : 'Scoped competition member'}</p>
        </div>
        <select className="input" value={role} onChange={e => setRole(e.target.value as CompetitionRole)}>
          {COMP_ROLE_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
        <button className={cn('btn-primary inline-flex items-center gap-2', MORPH)} onClick={() => updateMut.mutate()} disabled={updateMut.isPending}>
          <RefreshCw className="h-4 w-4" />
          {updateMut.isPending ? 'Saving…' : 'Update'}
        </button>
        <button className={cn('btn-ghost inline-flex items-center gap-2 text-red-300', MORPH)} onClick={() => removeMut.mutate()} disabled={removeMut.isPending || member.role === 'COMP_OWNER'}>
          <Trash2 className="h-4 w-4" />
          Remove
        </button>
      </div>
      <ErrorMsg msg={error} />
    </div>
  );
}

export function CompetitionMembersPanel({ compId, orgId }: { compId: string; orgId: string }) {
  const qc = useQueryClient();
  const [memberDiscordId, setMemberDiscordId] = useState('');
  const [memberRole, setMemberRole] = useState<CompetitionRole>('COMP_MEMBER');
  const [addError, setAddError] = useState('');

  const competitionMembersQuery = useQuery({
    queryKey: ['competitionMembers', compId],
    queryFn: () => api.competitionMembers(compId),
    enabled: Boolean(compId),
  });

  const refreshMembers = () => qc.invalidateQueries({ queryKey: ['competitionMembers', compId] });

  const addMut = useMutation({
    mutationFn: () => api.addCompetitionMember(compId, memberDiscordId, memberRole),
    onSuccess: () => {
      setMemberDiscordId('');
      setMemberRole('COMP_MEMBER');
      setAddError('');
      refreshMembers();
    },
    onError: (e: Error) => setAddError(parseApiError(e).message),
  });

  if (!compId) {
    return (
      <Card>
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-ink-secondary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Competition Members</h2>
        </div>
        <p className="text-sm text-ink-meta">Select a competition first.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="mb-4 flex items-center gap-2">
          <Plus className="h-4 w-4 text-ink-secondary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Add Competition Member</h2>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <DiscordMemberPicker
            value={memberDiscordId}
            onChange={setMemberDiscordId}
            search={q => api.searchOrgDiscordMembers(orgId, q)}
            disabled={!orgId}
            disabledHint="Select an org first"
            placeholder="Type a name…"
          />
          <select className="input" value={memberRole} onChange={e => setMemberRole(e.target.value as CompetitionRole)}>
            <option value="COMP_OWNER">Comp owner</option>
            <option value="COMP_ADMIN">Comp admin</option>
            <option value="COMP_MEMBER">Comp member</option>
          </select>
        </div>
        <button
          className={cn('btn-primary mt-4 inline-flex items-center gap-2', MORPH)}
          onClick={() => addMut.mutate()}
          disabled={!memberDiscordId.trim() || addMut.isPending}
        >
          <Plus className="h-4 w-4" />
          {addMut.isPending ? 'Saving…' : 'Assign Competition Role'}
        </button>
        <ErrorMsg msg={addError} />
      </Card>

      <Card>
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-ink-secondary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Competition Members</h2>
        </div>
        {competitionMembersQuery.isLoading ? <PageSpinner /> : (
          <div className="space-y-3">
            {(competitionMembersQuery.data || []).length === 0 ? (
              <p className="text-sm text-ink-meta">No competition members yet.</p>
            ) : (competitionMembersQuery.data || []).map(member => (
              <CompetitionMemberRow key={member.discordId} compId={compId} member={member} onDone={refreshMembers} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
